import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { jwtDecode } from 'jwt-decode';

// Story 28 — completely separate from AuthContext (tenant auth). Its own localStorage key,
// its own claims shape (username, role=SUPER_ADMIN, no restaurantId ever).
const TOKEN_KEY = 'admin_jwt_token';

const AdminAuthContext = createContext(null);

function parseToken(token) {
  try {
    const claims = jwtDecode(token);
    const now = Math.floor(Date.now() / 1000);
    if (claims.exp && claims.exp < now) return null;
    return claims;
  } catch {
    return null;
  }
}

const LOGGED_OUT = { token: null, username: null };

export function AdminAuthProvider({ children }) {
  const [authState, setAuthState] = useState(() => {
    const stored = localStorage.getItem(TOKEN_KEY);
    if (!stored) return LOGGED_OUT;
    const claims = parseToken(stored);
    if (!claims) {
      localStorage.removeItem(TOKEN_KEY);
      return LOGGED_OUT;
    }
    return { token: stored, username: claims.sub ?? null };
  });

  const login = useCallback((token) => {
    const claims = parseToken(token);
    if (!claims) return;
    localStorage.setItem(TOKEN_KEY, token);
    setAuthState({ token, username: claims.sub ?? null });
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setAuthState(LOGGED_OUT);
  }, []);

  const value = useMemo(
    () => ({ ...authState, isAuthenticated: !!authState.token, login, logout }),
    [authState, login, logout]
  );

  return <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>;
}

export function useAdminAuth() {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) throw new Error('useAdminAuth must be used within AdminAuthProvider');
  return ctx;
}

export { TOKEN_KEY as ADMIN_TOKEN_KEY };
